import { EntityMetadata, EntityModel } from '@msdk/definitions'
import React, { createContext, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { ModuleNotFoundError } from '../../../models'
import { useCompilationManifest, useModuleBaseMetadata } from '../../../state/base-metadata'
import { useEntityMetadataIncludeExternal } from '../../../state/metadata'
import { loadModule, providerFromManifest } from '../../../utils'
import { useRouteParams } from '../../hooks'

/**
 * A module can contain classes of custom logic/custom actions or
 * react components
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Module = Record<string, new (...args: any[]) => any | JSX.Element>

export interface ModuleCollection {
  /**
   * key the module name,
   * value: the loaded module
   */
  loadedModules: Record<string, Module>
  isLoading: boolean
  ensureModule: (
    entityName: string,
    moduleName: string,
    msProvider: string,
    model?: EntityModel,
    modalLoader?: boolean,
  ) => void
}

const INITIAL_STATE: ModuleCollection = {
  loadedModules: {},
  isLoading: false,
  ensureModule: (
    _entityName: string,
    _moduleName: string,
    _msProvider: string,
    _model?: EntityModel,
    _modalLoader?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
  ) => {},
}

const moduleFederationContext = createContext<ModuleCollection>(INITIAL_STATE)
export const useModuleFederationContext = () => useContext(moduleFederationContext)

export const ModuleFederationContextProvider = ({
  children,
  fallback,
}: {
  children: React.ReactNode
  fallback: React.ReactNode
}) => {
  const [moduleCollection, setModuleCollection] = useState<ModuleCollection>(INITIAL_STATE)
  const [loading, setLoading] = useState<boolean>(true)
  const [modalLoader, setModalLoader] = useState<boolean>(true)

  const { pathname } = useLocation()

  const params = useRouteParams()

  const moduleNameSanitized = (params?.moduleName ?? '').split(',')[0]
  const entityModel = useEntityMetadataIncludeExternal(params?.entityName, moduleNameSanitized)
  const baseMetadata = useModuleBaseMetadata(params?.entityName as string, moduleNameSanitized) as EntityMetadata
  const manifest = useCompilationManifest()

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const setLoader = (toggle: boolean, modalLoader?: boolean) => {
    if (modalLoader) {
      setModalLoader(toggle)
    } else {
      setLoading(toggle)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const loadMicroFrontEnd = async (provider: string, url?: string, modalLoader?: boolean) => {
    if (!moduleCollection.loadedModules[provider]) {
      setLoader(true, modalLoader)
      try {
        const res = await loadModule(
          url ?? `${window.__APP_CONFIG__.API}/${provider}/api/sync/bundle/remoteEntry.js`,
          'microfontend',
          './modules',
        )
        setModuleCollection((current) => ({ ...current, loadedModules: { ...current.loadedModules, [provider]: res } }))
      } catch (error) {
        console.error(`[ModuleFederationContextProvider] - error while loading module, provider: ${provider}`, error)
      }
    }
    setLoader(false, modalLoader)
  }

  /**
   * ensure that a micro frontend module is loaded
   * @param entityName the name of the entity
   * @param moduleName the name of the module (form, view, custom component)
   * @param msProvider the ms that has the requested resource
   * @param model the model of the entity, in case is a framework form/view
   * @param modalLoader if true the modal loader will be toggled, otherwise the page loader will be used
   */
  const ensureModule = (
    entityName: string,
    moduleName: string,
    msProvider: string,
    model?: EntityModel,
    // eslint-disable-next-line @typescript-eslint/no-shadow
    modalLoader?: boolean,
  ) => {
    /*
     * entity model exists, this is a normal route to a dynamic view/form
     * proceed to check for custom items and load module if needed
     */
    if (model) {
      const isCached = !!moduleCollection.loadedModules[msProvider]
      const hasCustomParts = model.hasCustomActions() || model.hasCustomLogic() || model.hasCustomFields()
      if (!isCached && hasCustomParts) {
        loadMicroFrontEnd(msProvider, undefined, modalLoader).catch(console.error)
      } else {
        setLoader(false, modalLoader)
      }
    } else if (entityName === 'CUSTOM') {
      /*
       * no entity model check if the entity name is 'CUSTOM'
       * in this case the entire component is custom, check for it in the manifest
       * and load the related module
       */
      try {
        const { provider, url } = providerFromManifest(manifest, moduleName)
        loadMicroFrontEnd(provider, url, modalLoader).catch(console.error)
      } catch (error) {
        if (error instanceof ModuleNotFoundError) {
          console.error(
            `[ModuleFederationContextProvider] no component with name ${moduleName} was found in the manifest, aborting module resolution.`,
            manifest,
          )
          setLoader(false, modalLoader)
        } else {
          throw error
        }
      }
    } else if (entityName !== 'CUSTOM' && moduleName && msProvider) {
      const isCached = !!moduleCollection.loadedModules[msProvider]
      if (!isCached) {
        loadMicroFrontEnd(msProvider, undefined, modalLoader).catch(console.error)
      } else {
        setLoader(false, modalLoader)
      }
    } else {
      /*
       * no entity model, no entity name 'CUSTOM' this is probably a shell normal route
       * do nothing
       */
      setLoader(false, modalLoader)
    }
  }

  useEffect(() => {
    ensureModule(params?.entityName as string, params?.moduleName as string, baseMetadata?.msProvider, entityModel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  return (
    <moduleFederationContext.Provider
      value={{ loadedModules: moduleCollection.loadedModules, isLoading: modalLoader, ensureModule }}
    >
      {loading && fallback}
      {!loading && children}
    </moduleFederationContext.Provider>
  )
}
